/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, LinearProgress, Link, TableCell, Typography } from '@mui/material';
import { ImageViewerV2, TableWidgetV2 } from '../../../components/v2';
import TableService from '../../../services/TableService';
import { convertTimeToMinutes, isValidTime } from 'utils';

const idOf = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value.id || null;
  return value;
};

const horarioCampo = (inspecionadoEm) => {
  const parsed = moment.parseZone(inspecionadoEm);
  if (!parsed.isValid()) return '—';
  return parsed.format('HH:mm');
};

const leituraPrimeira = (inicioJornada, primeira) => {
  if (!primeira?.inspecionadoEm) return null;
  const campo = horarioCampo(primeira.inspecionadoEm);
  const inicio = inicioJornada || '—';
  if (!isValidTime(inicioJornada)) {
    return `Início da jornada ${inicio}. Primeira inspeção ${campo}.`;
  }
  const parsed = moment.parseZone(primeira.inspecionadoEm);
  const diff = (parsed.hours() * 60 + parsed.minutes()) - convertTimeToMinutes(inicioJornada);
  if (diff > 0) {
    return `Início da jornada ${inicio}. Primeira inspeção ${campo}. Ponto → 1ª inspeção: +${diff} min.`;
  }
  if (diff < 0) {
    return `Início da jornada ${inicio}. Primeira inspeção ${campo}. A inspeção começou ${Math.abs(diff)} min antes do ponto.`;
  }
  return `Início da jornada ${inicio}. Primeira inspeção ${campo}. No mesmo minuto.`;
};

const servicoLabel = (row) => (
  [row.nsirTipoServico, row.nsirSubtipo].filter(Boolean).join(' · ')
);

const ServicoCell = ({ row }) => {
  const detalhe = [row.nsirSubtipo, row.nsirEquipamento].filter(Boolean).join(' · ');
  return (
    <Box>
      <Typography variant="body2" component="div">
        {row.nsirTipoServico || '—'}
      </Typography>
      {detalhe ? (
        <Typography variant="caption" component="div" color="text.secondary">
          {detalhe}
        </Typography>
      ) : null}
    </Box>
  );
};

ServicoCell.propTypes = {
  row: PropTypes.object.isRequired,
};

const nsirHref = (row) => (row.nsirId ? `/v2/nsir/${row.nsirId}` : null);

const nsmpHref = (row) => {
  if (!row.nsirId || !row.nsmpId) return null;
  return `/v2/nsir/${row.nsirId}?nsmpId=${encodeURIComponent(row.nsmpId)}`;
};

const inspecaoLinks = (row) => [
  row.nsirId ? { label: `NSIR ${row.nsirCodigo || ''}`.trim(), href: nsirHref(row) } : null,
  row.nsmpId ? { label: `NSMP ${row.nsmpCodigo || ''}`.trim(), href: nsmpHref(row) } : null,
].filter((link) => link?.href);

const buildGallery = (rows) => {
  const images = [];
  const startById = {};
  rows.forEach((row) => {
    const urls = (row.fotosUrls || []).filter(Boolean);
    startById[row.galleryKey] = images.length;
    const horario = horarioCampo(row.inspecionadoEm);
    const servico = servicoLabel(row);
    urls.forEach((url, photoIndex) => {
      images.push({
        src: url,
        nome: [horario, servico || row.nsirCodigo || 'Inspeção'].filter(Boolean).join(' · '),
        alt: `Foto ${photoIndex + 1} de ${urls.length}`,
        links: inspecaoLinks(row),
      });
    });
  });
  return { images, startById };
};

const Fotos = ({ urls, onOpen }) => {
  if (!urls?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sem foto
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {urls.map((url, index) => (
        <Box
          key={`${url}-${index}`}
          component="button"
          type="button"
          aria-label={`Ver foto ${index + 1}`}
          onClick={() => onOpen(index)}
          sx={{
            p: 0,
            border: 0,
            lineHeight: 0,
            cursor: 'pointer',
            bgcolor: 'transparent',
            borderRadius: 0.5,
            '&:hover img': { opacity: 0.85 },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 1,
            },
          }}
        >
          <Box
            component="img"
            alt=""
            src={url}
            sx={{
              width: 48,
              height: 36,
              objectFit: 'cover',
              borderRadius: 0.5,
              display: 'block',
              bgcolor: 'action.hover',
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

Fotos.propTypes = {
  urls: PropTypes.arrayOf(PropTypes.string),
  onOpen: PropTypes.func.isRequired,
};

const InspecoesDia = ({
  usuarioId,
  dia,
  tipoServicoId,
  inicioJornada,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const usuario = idOf(usuarioId);
  const tipoServico = idOf(tipoServicoId);
  const diaIso = dia ? moment(dia).format('YYYY-MM-DD') : null;

  useEffect(() => {
    if (!usuario || !diaIso) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setErro(false);
      try {
        const body = { usuarioId: usuario, dia: diaIso };
        if (tipoServico) body.tipoServicoId = tipoServico;
        const response = await TableService.request('jornadas/conferencia-deslocamento', body);
        const data = response?.data;
        const list = Array.isArray(data) ? data : (data?.content || []);
        if (!cancelled) {
          setRows(list.map((item, index) => ({
            ...item,
            id: item.nsmpId || item.nsirId || `${diaIso}-${index}`,
            galleryKey: `${diaIso}-${index}`,
          })));
        }
      } catch {
        if (!cancelled) {
          setRows([]);
          setErro(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [usuario, diaIso, tipoServico]);

  const gallery = useMemo(() => buildGallery(rows), [rows]);

  const openFoto = (rowId, photoIndex) => {
    const start = gallery.startById[rowId] || 0;
    setViewerIndex(start + photoIndex);
    setViewerOpen(true);
  };

  const renderRows = (row) => (
    <>
      <TableCell>{horarioCampo(row.inspecionadoEm)}</TableCell>
      <TableCell>
        {row.nsirId ? (
          <Link
            href={`/v2/nsir/${row.nsirId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.nsirCodigo || 'Abrir'}
          </Link>
        ) : (row.nsirCodigo || '—')}
      </TableCell>
      <TableCell><ServicoCell row={row} /></TableCell>
      <TableCell>
        {nsmpHref(row) ? (
          <Link
            href={nsmpHref(row)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.nsmpCodigo || 'Abrir'}
          </Link>
        ) : (row.nsmpCodigo || '—')}
      </TableCell>
      <TableCell>
        <Fotos
          urls={row.fotosUrls}
          onOpen={(photoIndex) => openFoto(row.galleryKey, photoIndex)}
        />
      </TableCell>
    </>
  );

  if (!usuario || !diaIso) {
    return (
      <Typography variant="body2" color="text.secondary">
        Abra uma jornada com inspetor e data para ver as inspeções do dia.
      </Typography>
    );
  }

  return (
    <Box>
      {loading ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {erro ? (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Não foi possível carregar as inspeções deste dia.
        </Typography>
      ) : null}
      {!loading && !erro && rows.length > 0 ? (
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {leituraPrimeira(inicioJornada, rows[0])}
        </Typography>
      ) : null}
      {!loading ? (
        <TableWidgetV2
          bare
          size="small"
          collapse={false}
          showEmptyState={!erro}
          emptyStateTitle="Sem inspeção neste dia"
          emptyStateMessage="Nenhuma inspeção de campo para este inspetor nesta data."
          titles={['Horário', 'NSIR', 'Serviço', 'NSMP', 'Fotos']}
          rows={rows}
          renderRows={renderRows}
        />
      ) : null}
      <ImageViewerV2
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        images={gallery.images}
        activeIndex={viewerIndex}
      />
    </Box>
  );
};

InspecoesDia.propTypes = {
  usuarioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  dia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tipoServicoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  inicioJornada: PropTypes.string,
};

export default InspecoesDia;
